import React, { useRef, useState } from 'react';
import {
  X, User, Mail, Phone, Building2, Globe, Upload, FileText, Loader2, Check,
  ShieldCheck, DollarSign, MapPin, EyeOff, FileArchive, Trash2, Files,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const empty = {
  name: '', email: '', company: '', phone_code: '+1', phone: '',
  country: 'Canada', city: '', years: '', notes: '',
  commission_per_student: 500, status: 'agreement_signed',
};

const COMPANY_DOC_ACCEPT = [
  'application/pdf', '.pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.doc', '.docx',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xls', '.xlsx',
].join(',');

const ALLOWED_EXT = /\.(pdf|jpe?g|png|webp|heic|heif|docx?|xlsx?)$/i;

const humanSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const CreateRecruiterModal = ({ onClose, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [companyDocs, setCompanyDocs] = useState([]); // File[]
  const [silent, setSilent] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const companyDocRef = useRef(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'Only PDF files are allowed' });
      return;
    }
    if (f.size > 20 * 1024 * 1024) { toast({ title: 'File exceeds 20MB' }); return; }
    setFile(f);
  };

  const onPickCompanyDocs = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    const accepted = [];
    for (const f of picked) {
      if (!ALLOWED_EXT.test(f.name)) {
        toast({ title: 'Unsupported file', description: `${f.name} — allowed: PDF, images, Word, Excel.` });
        continue;
      }
      if (f.size > 20 * 1024 * 1024) {
        toast({ title: 'Too large', description: `${f.name} exceeds 20MB.` });
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setCompanyDocs((xs) => [...xs, ...accepted]);
  };

  const removeCompanyDoc = (idx) => {
    setCompanyDocs((xs) => xs.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.phone || !form.country) {
      toast({ title: 'Missing required fields' });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      fd.append('silent', silent ? 'true' : 'false');
      if (file) fd.append('signed_agreement_pdf', file);
      companyDocs.forEach((f) => fd.append('company_documents', f));
      const { data } = await api.post('/admin/recruiters/create-direct', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Recruiter created',
        description: silent
          ? 'Saved as Internal record — recruiter was not notified.'
          : form.status === 'active'
            ? 'Login credentials emailed.'
            : 'Ready to activate when commission is confirmed.',
      });
      setBusy(false);
      if (onCreated) onCreated(data); else if (onClose) onClose();
    } catch (err) {
      toast({ title: 'Creation failed', description: err?.response?.data?.detail || 'Error' });
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="create-recruiter-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl z-[61]">
        <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Add Recruiter</div>
            <h3 className="font-display text-xl text-[#0B5E2A]">Create profile & attach signed agreement</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="create-recruiter-close" className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5 pointer-events-none" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-start gap-2.5 bg-[#DEE9FA] text-[#1e40af] rounded-lg p-3 text-sm">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Use this for recruiters you've onboarded in person. The standard DocuSign/email signup flow is skipped. Optionally attach the signed PDF and company documents.</span>
          </div>

          {/* Silent (internal) toggle */}
          <label
            className={`flex items-start gap-3 rounded-xl border-2 p-3.5 cursor-pointer transition-colors ${
              silent ? 'border-[#6b21a8] bg-[#F5EDFC]' : 'border-[#0B5E2A]/15 hover:bg-[#f5f9f3]'
            }`}
            data-testid="cr-silent-toggle-label"
          >
            <input
              type="checkbox"
              checked={silent}
              onChange={(e) => setSilent(e.target.checked)}
              data-testid="cr-silent-toggle"
              className="mt-0.5 h-4 w-4 accent-[#6b21a8]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-semibold text-sm text-[#3b1a5c]">
                <EyeOff className="h-4 w-4" /> Create silently (do not notify the recruiter)
              </div>
              <div className="text-xs text-[#6b21a8]/90 mt-1">
                Saved as an <span className="font-bold">Internal / Manual</span> record. No welcome email, no magic link — the recruiter has no login. You can still tag Student Aid records with this agent. Activate later to grant portal access.
              </div>
            </div>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name *" icon={User}><input required value={form.name} onChange={onChange('name')} data-testid="cr-name" className={inputIconCls} /></Field>
            <Field label="Email *" icon={Mail}><input required type="email" value={form.email} onChange={onChange('email')} data-testid="cr-email" className={inputIconCls} /></Field>
            <Field label="Company *" icon={Building2}><input required value={form.company} onChange={onChange('company')} data-testid="cr-company" className={inputIconCls} /></Field>
            <div className="grid grid-cols-4 gap-2">
              <Field label="Code">
                <input value={form.phone_code} onChange={onChange('phone_code')} className={inputCls} />
              </Field>
              <div className="col-span-3">
                <Field label="Phone *" icon={Phone}><input required value={form.phone} onChange={onChange('phone')} data-testid="cr-phone" className={inputIconCls} /></Field>
              </div>
            </div>
            <Field label="Country *" icon={Globe}><input required value={form.country} onChange={onChange('country')} className={inputIconCls} /></Field>
            <Field label="City" icon={MapPin}><input value={form.city} onChange={onChange('city')} className={inputIconCls} /></Field>
            <Field label="Experience (years)"><input value={form.years} onChange={onChange('years')} className={inputCls} /></Field>
            <Field label="Commission per Student ($)" icon={DollarSign}>
              <input type="number" min="0" step="50" value={form.commission_per_student} onChange={onChange('commission_per_student')} data-testid="cr-commission" className={inputIconCls} />
            </Field>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Notes (private)</label>
            <textarea rows={3} value={form.notes} onChange={onChange('notes')} className={`${inputCls} resize-y`} />
          </div>

          {/* Signed Agreement */}
          <div>
            <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Signed Agreement (PDF, optional)</label>
            <div
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
              role="button"
              tabIndex={0}
              data-testid="cr-signed-agreement-dropzone"
              className="block border-2 border-dashed border-[#0B5E2A]/25 rounded-xl p-5 text-center cursor-pointer bg-[#f5f9f3] hover:bg-[#edf6ea] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
            >
              <Upload className="h-5 w-5 brand-green mx-auto pointer-events-none" />
              {!file ? (
                <>
                  <div className="mt-2 font-semibold text-[#0B5E2A] text-sm pointer-events-none">Attach signed PDF (recommended for in-person signups)</div>
                  <div className="text-xs text-[#6b7e6d] mt-1 pointer-events-none">or leave blank and upload later</div>
                </>
              ) : (
                <div className="mt-2 flex items-center justify-center gap-2 text-sm pointer-events-none">
                  <FileText className="h-4 w-4 brand-green" />
                  <span className="font-semibold text-[#0B5E2A]">{file.name}</span>
                  <span className="text-xs text-[#6b7e6d]">({Math.ceil(file.size / 1024)} KB)</span>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPickFile} data-testid="cr-signed-agreement-input" />
            {file && (
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="mt-2 text-xs font-semibold text-[#9b1c1c] hover:underline">
                Remove file
              </button>
            )}
          </div>

          {/* Company Documents (multi) */}
          <div>
            <label className="block text-sm font-medium text-[#2e4030] mb-1.5 flex items-center gap-1.5">
              <Files className="h-4 w-4 text-[#0B5E2A]" /> Company Documents (optional)
              <span className="text-[11px] font-normal text-[#7a8a7c]">— incorporation, licenses, void cheque, IDs, tax forms, etc.</span>
            </label>
            <div
              onClick={() => companyDocRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); companyDocRef.current?.click(); } }}
              role="button"
              tabIndex={0}
              data-testid="cr-company-docs-dropzone"
              className="block border-2 border-dashed border-[#0B5E2A]/25 rounded-xl p-5 text-center cursor-pointer bg-[#f5f9f3] hover:bg-[#edf6ea] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
            >
              <FileArchive className="h-5 w-5 brand-green mx-auto pointer-events-none" />
              <div className="mt-2 font-semibold text-[#0B5E2A] text-sm pointer-events-none">
                {companyDocs.length === 0 ? 'Add company documents' : 'Add more files'}
              </div>
              <div className="text-xs text-[#6b7e6d] mt-1 pointer-events-none">
                PDF · JPG · PNG · Word · Excel — up to 20 MB each
              </div>
            </div>
            <input
              ref={companyDocRef}
              type="file"
              multiple
              accept={COMPANY_DOC_ACCEPT}
              className="hidden"
              onChange={onPickCompanyDocs}
              data-testid="cr-company-docs-input"
            />
            {companyDocs.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="cr-company-docs-list">
                {companyDocs.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-[#f7faf5] border border-[#0B5E2A]/10 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 brand-green shrink-0" />
                      <span className="font-medium text-[#0B5E2A] truncate" title={f.name}>{f.name}</span>
                      <span className="text-[11px] text-[#6b7e6d] shrink-0">({humanSize(f.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCompanyDoc(idx)}
                      data-testid={`cr-company-doc-remove-${idx}`}
                      className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Start-as radios (disabled in silent mode) */}
          {!silent && (
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Start as</label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${form.status === 'agreement_signed' ? 'border-[#0B5E2A] bg-[#f0f6ee]' : 'border-[#0B5E2A]/20 hover:bg-[#f5f9f3]'}`}>
                  <input type="radio" name="status" value="agreement_signed" checked={form.status === 'agreement_signed'} onChange={onChange('status')} className="sr-only" data-testid="cr-status-signed" />
                  <div className="font-semibold text-sm text-[#0B5E2A]">Agreement Signed</div>
                  <div className="text-xs text-[#6b7e6d] mt-0.5">Recommended. Click "Activate" later to email credentials.</div>
                </label>
                <label className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${form.status === 'active' ? 'border-[#0B5E2A] bg-[#f0f6ee]' : 'border-[#0B5E2A]/20 hover:bg-[#f5f9f3]'}`}>
                  <input type="radio" name="status" value="active" checked={form.status === 'active'} onChange={onChange('status')} className="sr-only" data-testid="cr-status-active" />
                  <div className="font-semibold text-sm text-[#0B5E2A]">Active (email login now)</div>
                  <div className="text-xs text-[#6b7e6d] mt-0.5">Creates + emails temp credentials immediately.</div>
                </label>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-3 border-t border-[#0B5E2A]/10">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="cr-submit" className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>) : (<><Check className="h-4 w-4 pointer-events-none" /> Create Recruiter</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30';
const inputIconCls = 'w-full pl-10 pr-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30';

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <div className={`relative ${Icon ? 'icon-field' : ''}`}>
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d] pointer-events-none z-10" />}
      {children}
    </div>
  </div>
);

export default CreateRecruiterModal;
